import { useEffect, useId, useState } from 'react';
import { Alert, Button, FormField, Modal } from '../../ui';

const emptyForm = {
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  phone_number: '',
};

const invitePayload = ({ middle_name: middleName, ...required }) =>
  middleName.trim() ? { ...required, middle_name: middleName.trim() } : required;

export default function InviteSubMerchantUserModal({ isOpen, subMerchantName, onClose, onSubmit }) {
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fieldId = useId();
  const formId = `${fieldId}-form`;

  useEffect(() => {
    setForm(emptyForm);
    setFieldErrors({});
    setError('');
  }, [isOpen]);

  const setField = (name) => (e) => setForm({ ...form, [name]: e.target.value });
  const fieldError = (name) => fieldErrors[name]?.[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);
    try {
      await onSubmit(invitePayload(form));
      onClose();
    } catch (err) {
      const data = err.response?.data;
      setFieldErrors(data?.errors || {});
      setError(data?.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite a user"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={loading}>
            Send Invite
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form id={formId} onSubmit={handleSubmit} noValidate>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <FormField label="First Name" htmlFor={`${fieldId}-first`} error={fieldError('first_name')} required>
            <input
              id={`${fieldId}-first`}
              className="form-control"
              value={form.first_name}
              onChange={setField('first_name')}
              required
            />
          </FormField>

          <FormField label="Last Name" htmlFor={`${fieldId}-last`} error={fieldError('last_name')} required>
            <input
              id={`${fieldId}-last`}
              className="form-control"
              value={form.last_name}
              onChange={setField('last_name')}
              required
            />
          </FormField>
        </div>

        <FormField label="Middle Name" htmlFor={`${fieldId}-middle`} error={fieldError('middle_name')} hint="Optional.">
          <input
            id={`${fieldId}-middle`}
            className="form-control"
            value={form.middle_name}
            onChange={setField('middle_name')}
          />
        </FormField>

        <FormField
          label="Email"
          htmlFor={`${fieldId}-email`}
          error={fieldError('email')}
          hint="The invitation and their password are sent here."
          required
        >
          <input
            id={`${fieldId}-email`}
            type="email"
            className="form-control"
            value={form.email}
            onChange={setField('email')}
            required
          />
        </FormField>

        <FormField label="Phone Number" htmlFor={`${fieldId}-phone`} error={fieldError('phone_number')} required>
          <input
            id={`${fieldId}-phone`}
            className="form-control"
            maxLength={15}
            value={form.phone_number}
            onChange={setField('phone_number')}
            required
          />
        </FormField>

        <p className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          We email them a generated password. They sign in to the partner dashboard and see only
          {subMerchantName ? ` ${subMerchantName}` : ' this sub-merchant'}.
        </p>
      </form>
    </Modal>
  );
}
